import React, { useState } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, convertFromMXN } from '../../utils/formatters';
import { User, Phone, Mail, UserCheck, DollarSign, CreditCard, Calendar, AlertCircle, ChevronRight, ChevronLeft, Shield, Clock, RefreshCw, Settings, Plus, Minus, Edit, Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { calculatePaymentData } from '../paymentCalculator/utils/calculations';
import { Toggle } from '../ui/Toggle';

interface ReinvestmentClientInfoProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ReinvestmentClientInfo: React.FC<ReinvestmentClientInfoProps> = ({ onNext, onPrevious }) => {
  const { investment, updateInvestment } = useCalculator();
  
  // Estados para edición de configuración de pagos
  const [isEditingDownPayment, setIsEditingDownPayment] = useState(false);
  const [isEditingInstallments, setIsEditingInstallments] = useState(false);
  const [tempDownPaymentPercentage, setTempDownPaymentPercentage] = useState(investment.downPaymentPercentage || 30);
  const [tempInstallments, setTempInstallments] = useState(investment.downPaymentInstallments || 1);

  // Calculate payment data
  const paymentData = calculatePaymentData(investment);
  const totalInvestment = investment.initialCertificates * investment.certificateBasePrice;
  const downPaymentAmount = totalInvestment * (investment.downPaymentPercentage || 30) / 100;
  const totalWithInterest = downPaymentAmount + paymentData.amortizationTable.reduce((sum, entry) => sum + entry.paymentAmount, 0);

  // Funciones para manejar edición
  const handleSaveDownPayment = () => {
    if (tempDownPaymentPercentage >= 10 && tempDownPaymentPercentage <= 100) {
      updateInvestment({ downPaymentPercentage: tempDownPaymentPercentage });
      setIsEditingDownPayment(false);
    }
  };

  const handleCancelDownPayment = () => {
    setTempDownPaymentPercentage(investment.downPaymentPercentage || 30);
    setIsEditingDownPayment(false);
  };

  const handleSaveInstallments = () => {
    if (tempInstallments >= 1 && tempInstallments <= 6) {
      updateInvestment({ downPaymentInstallments: tempInstallments });
      setIsEditingInstallments(false);
    }
  };

  const handleCancelInstallments = () => {
    setTempInstallments(investment.downPaymentInstallments || 1);
    setIsEditingInstallments(false);
  };

  // Función para proceder al siguiente paso
  const handleContinue = () => {
    // Validar que al menos el nombre esté completo
    if (!investment.investorName || investment.investorName.trim() === '') {
      alert('Por favor ingresa el nombre del inversionista para continuar');
      return;
    }
    onNext();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Información del Cliente y Ejecutivo</h2>
              <p className="text-blue-100">Complete los datos para personalizar su experiencia de inversión</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Apartado con Stripe */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-200 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-800">Apartado de Certificados</h3>
                <p className="text-sm text-blue-600">Asegura tu precio por 10 días mientras revisas la documentación</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                  <h4 className="font-medium text-gray-800">Apartado Seguro</h4>
                </div>
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-green-700 mb-2">$5,000 MXN</div>
                  <div className="text-sm text-gray-600">Pago único de apartado</div>
                </div>
                <button 
                  onClick={() => window.open('https://inversion.citruspatrimonial.com/b/14k9EAdvE78T0N26oN', '_blank')}
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-300"
                >
                  Apartar con Tarjeta
                </button>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-medium text-blue-800">Beneficios del Apartado:</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Clock className="w-3 h-3 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Precio Garantizado por 10 Días</p>
                      <p className="text-xs text-blue-600">El precio del certificado queda fijo durante el período de revisión</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Shield className="w-3 h-3 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-800">Tiempo para Revisar Documentación</p>
                      <p className="text-xs text-green-600">10 días completos para analizar contratos y términos</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <RefreshCw className="w-3 h-3 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-purple-800">Reembolso Garantizado</p>
                      <p className="text-xs text-purple-600">Si decides no continuar, se reembolsa el 100% antes de 10 días</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>Nota:</strong> El apartado se descuenta del enganche final. Es una forma segura de asegurar tu inversión mientras tomas la decisión final.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Métodos de Pago Aceptados</h4>
              <div className="text-sm text-green-700 space-y-3">
                <div>
                  <div className="font-bold text-green-800 mb-1">FIDECOMISO 697 - SOLO TRANSFERENCIA</div>
                  <div><strong>Banco:</strong> INSTITUCIÓN DE BANCA MÚLTIPLE, GRUPO FINANCIERO VE POR MÁS</div>
                  <div><strong>No. de Cuenta:</strong> 0000070758</div>
                  <div><strong>CLABE:</strong> 113180000007017586</div>
                </div>
                <div>
                  <div className="font-bold text-green-800 mb-1">BBVA MÉXICO - TRANSFERENCIA Y EFECTIVO</div>
                  <div><strong>No. de Cuenta:</strong> 0118891737</div>
                  <div><strong>CLABE:</strong> 012910001188917370</div>
                </div>
                <div>
                  <div className="font-bold text-green-800 mb-1">SANTANDER - TRANSFERENCIA Y EFECTIVO</div>
                  <div><strong>No. de Cuenta:</strong> 65 50166596</div>
                  <div><strong>CLABE:</strong> 646017230197572477</div>
                </div>
                <div>
                  <div className="font-bold text-green-800 mb-1">PAGO CON TARJETA</div>
                  <div>
                    <strong>Link:</strong>{' '}
                    <a 
                      href="https://inversion.citruspatrimonial.com/b/14k9EAdvE78T0N26oN" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Pagar con Tarjeta
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Cuentas en Dólares</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <div><strong>Banco:</strong> JP MORGAN CHASE BANK N.A.</div>
                <div><strong>No. de Cuenta:</strong> 336794722</div>
                <div><strong>ABA (ACH):</strong> 111000614</div>
                <div><strong>Routing No. (Wire Transfer):</strong> 21000021</div>
              </div>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-2">Contacto</h4>
              <div className="text-sm text-purple-700 space-y-1">
                <div><strong>WhatsApp:</strong> +52 55 1000 0615</div>
                <div><strong>Email:</strong> inversiones@cosechacapital.com</div>
                <div><strong>Horario:</strong> Lun-Vie 9:00-18:00</div>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Información del Cliente</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={investment.investorName || ''}
                    onChange={(e) => updateInvestment({ investorName: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nombre del inversionista"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={investment.investorPhone || ''}
                    onChange={(e) => updateInvestment({ investorPhone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+52 55 1234 5678"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={investment.investorEmail || ''}
                    onChange={(e) => updateInvestment({ investorEmail: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="cliente@email.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Executive Information */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Información del Ejecutivo</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Ejecutivo
                </label>
                <div className="relative">
                  <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={investment.executiveName || ''}
                    onChange={(e) => updateInvestment({ executiveName: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Nombre del ejecutivo"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono Ejecutivo
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={investment.executivePhone || ''}
                    onChange={(e) => updateInvestment({ executivePhone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="+52 55 1000 0615"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Ejecutivo
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={investment.executiveEmail || ''}
                    onChange={(e) => updateInvestment({ executiveEmail: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="ejecutivo@cosechacapital.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Configuration */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Configuración de Pagos</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Down Payment Percentage */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Porcentaje de Enganche</label>
                  {!isEditingDownPayment ? (
                    <button
                      onClick={() => {
                        setIsEditingDownPayment(true);
                        setTempDownPaymentPercentage(investment.downPaymentPercentage || 30);
                      }}
                      className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                      title="Editar porcentaje de enganche"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  ) : (
                    <div className="flex gap-1">
                      <button
                        onClick={handleSaveDownPayment}
                        className="p-1 text-green-600 hover:text-green-700 transition-colors"
                        title="Guardar cambios"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelDownPayment}
                        className="p-1 text-red-600 hover:text-red-700 transition-colors"
                        title="Cancelar edición"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {!isEditingDownPayment ? (
                  <div className="text-center p-4 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
                       onClick={() => {
                         setIsEditingDownPayment(true);
                         setTempDownPaymentPercentage(investment.downPaymentPercentage || 30);
                       }}>
                    <div className="text-3xl font-bold text-purple-700 mb-1">
                      {investment.downPaymentPercentage || 30}%
                    </div>
                    <div className="text-sm text-purple-600">del valor total</div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="10"
                        max="100"
                        value={tempDownPaymentPercentage}
                        onChange={(e) => setTempDownPaymentPercentage(parseInt(e.target.value) || 30)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveDownPayment();
                          if (e.key === 'Escape') handleCancelDownPayment();
                        }}
                        className="flex-1 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        autoFocus
                      />
                      <span className="text-purple-700 font-medium">%</span>
                    </div>
                    
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={tempDownPaymentPercentage}
                      onChange={(e) => setTempDownPaymentPercentage(parseInt(e.target.value))}
                      className="w-full h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg appearance-none cursor-pointer"
                    />
                    
                    <div className="grid grid-cols-4 gap-2">
                      {[30, 50, 70, 100].map((percentage) => (
                        <button
                          key={percentage}
                          onClick={() => setTempDownPaymentPercentage(percentage)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            tempDownPaymentPercentage === percentage
                              ? 'bg-purple-600 text-white'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          }`}
                        >
                          {percentage}%
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="text-center text-sm text-gray-600">
                  Enganche: {formatCurrency(convertFromMXN(totalInvestment * (tempDownPaymentPercentage || investment.downPaymentPercentage || 30) / 100, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
              </div>
              
              {/* Down Payment Installments */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Pagos del Enganche</label>
                  {!isEditingInstallments ? (
                    <button
                      onClick={() => {
                        setIsEditingInstallments(true);
                        setTempInstallments(investment.downPaymentInstallments || 1);
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Editar número de pagos del enganche"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  ) : (
                    <div className="flex gap-1">
                      <button
                        onClick={handleSaveInstallments}
                        className="p-1 text-green-600 hover:text-green-700 transition-colors"
                        title="Guardar cambios"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelInstallments}
                        className="p-1 text-red-600 hover:text-red-700 transition-colors"
                        title="Cancelar edición"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {!isEditingInstallments ? (
                  <div className="text-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                       onClick={() => {
                         setIsEditingInstallments(true);
                         setTempInstallments(investment.downPaymentInstallments || 1);
                       }}>
                    <div className="text-3xl font-bold text-blue-700 mb-1">
                      {investment.downPaymentInstallments || 1}
                    </div>
                    <div className="text-sm text-blue-600">
                      {(investment.downPaymentInstallments || 1) === 1 ? 'pago único' : 'pagos'}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="6"
                        value={tempInstallments}
                        onChange={(e) => setTempInstallments(parseInt(e.target.value) || 1)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveInstallments();
                          if (e.key === 'Escape') handleCancelInstallments();
                        }}
                        className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <span className="text-blue-700 font-medium">pagos</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3].map((installments) => (
                        <button
                          key={installments}
                          onClick={() => setTempInstallments(installments)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            tempInstallments === installments
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          {installments}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="text-center text-sm text-gray-600">
                  {(tempInstallments || investment.downPaymentInstallments || 1) > 1 && (
                    <>Por pago: {formatCurrency(convertFromMXN(totalInvestment * (tempDownPaymentPercentage || investment.downPaymentPercentage || 30) / 100 / (tempInstallments || investment.downPaymentInstallments || 1), investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-green-600" />
                <h4 className="font-medium text-green-800">Proceso de Apartado</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-medium text-green-700">Paso 1</div>
                  <div className="text-green-600">Pago de apartado $5,000</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-green-700">Paso 2</div>
                  <div className="text-green-600">Revisión de documentos (10 días)</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-green-700">Paso 3</div>
                  <div className="text-green-600">Pago de enganche o reembolso</div>
                </div>
              </div>
            </div>
          </div>

          {/* Investment Summary with Payment Details */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-200 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-700" />
              </div>
              <h3 className="text-lg font-semibold text-green-800">Resumen de Inversión</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="text-sm text-gray-600 mb-1">Certificados</div>
                <div className="text-xl font-bold text-green-700">
                  {investment.initialCertificates}
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="text-sm text-gray-600 mb-1">Inversión Total</div>
                <div className="text-xl font-bold text-blue-700">
                  {formatCurrency(convertFromMXN(totalInvestment, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="text-sm text-gray-600 mb-1">Enganche ({investment.downPaymentPercentage || 30}%)</div>
                <div className="text-xl font-bold text-purple-700">
                  {formatCurrency(convertFromMXN(totalInvestment * (investment.downPaymentPercentage || 30) / 100, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  {(investment.downPaymentInstallments || 1) === 1 ? 'Pago único' : `${investment.downPaymentInstallments} pagos`}
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <div className="text-sm text-gray-600 mb-1">Mensualidad</div>
                <div className="text-xl font-bold text-orange-700">
                  {formatCurrency(convertFromMXN(paymentData.totalFinanced / 48, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
                <div className="text-xs text-orange-600 mt-1">48 mensualidades</div>
              </div>
            </div>
            
            {/* Payment Schedule Toggle */}
            <div className="mt-6 p-4 bg-white rounded-xl shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-gray-800">Cronograma Personalizado</h4>
                  <p className="text-sm text-gray-600">Personaliza las fechas de pago según tus necesidades</p>
                </div>
                <Toggle
                  checked={investment.enableCustomDownPaymentSchedule || false}
                  onChange={() => updateInvestment({ enableCustomDownPaymentSchedule: !investment.enableCustomDownPaymentSchedule })}
                  label=""
                />
              </div>
              
              {investment.enableCustomDownPaymentSchedule && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    ✅ Cronograma personalizado activado. Podrás configurar fechas específicas para cada pago en los siguientes pasos.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Amortization Table Preview */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Vista Previa - Tabla de Amortización</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                <div className="text-sm text-green-600 mb-1">Enganche</div>
                <div className="text-xl font-bold text-green-700">
                  {formatCurrency(convertFromMXN(downPaymentAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                <div className="text-sm text-blue-600 mb-1">A Financiar</div>
                <div className="text-xl font-bold text-blue-700">
                  {formatCurrency(convertFromMXN(paymentData.totalFinanced, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                <div className="text-sm text-purple-600 mb-1">Mensualidades</div>
                <div className="text-xl font-bold text-purple-700">
                  48 pagos
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
                <div className="text-sm text-orange-600 mb-1">Total a Pagar</div>
                <div className="text-xl font-bold text-orange-700">
                  {formatCurrency(convertFromMXN(totalWithInterest, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                </div>
              </div>
            </div>

            {/* Sample Amortization Table (first 5 payments) */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pago #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pago Total
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Capital
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Saldo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Down Payment Row */}
                  <tr className="bg-green-50 font-medium">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-700">
                      0
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700">
                      {new Date().toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-700">
                      ENGANCHE
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                      {formatCurrency(convertFromMXN(downPaymentAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                      {formatCurrency(convertFromMXN(downPaymentAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-green-700">
                      {formatCurrency(convertFromMXN(paymentData.totalFinanced, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                    </td>
                  </tr>
                  
                  {/* First 5 Monthly Payments */}
                  {paymentData.amortizationTable.slice(0, 5).map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.paymentNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {entry.date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        Mensualidad
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(convertFromMXN(entry.paymentAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                        {formatCurrency(convertFromMXN(entry.principalAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(convertFromMXN(entry.remainingBalance, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                      </td>
                    </tr>
                  ))}
                  
                  {/* Dots row */}
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-4 py-3 text-center text-sm text-gray-500">
                      ... {paymentData.amortizationTable.length - 5} pagos adicionales ...
                    </td>
                  </tr>
                  
                  {/* Final payment */}
                  {paymentData.amortizationTable.length > 5 && (
                    <tr className="bg-blue-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {paymentData.amortizationTable[paymentData.amortizationTable.length - 1].paymentNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {paymentData.amortizationTable[paymentData.amortizationTable.length - 1].date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        Último Pago
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(convertFromMXN(paymentData.amortizationTable[paymentData.amortizationTable.length - 1].paymentAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                        {formatCurrency(convertFromMXN(paymentData.amortizationTable[paymentData.amortizationTable.length - 1].principalAmount, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600">
                        {formatCurrency(0, investment.currencyFormat)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-800">Información Importante</h4>
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• La tabla completa de amortización estará disponible en el reporte final</li>
                <li>• Los pagos se realizan mensualmente durante 48 meses</li>
                <li>• El enganche se paga al momento de la firma del contrato</li>
                <li>• Todos los pagos están expresados en {investment.currencyFormat}</li>
              </ul>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Anterior</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContinue}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReinvestmentClientInfo;